import { useEffect, useState, useCallback, useMemo } from "react";
import { Minus, Plus, Trash2, ShoppingBag, Truck, Shield, Loader2 } from "lucide-react";
import Select from "react-select";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";

export default function CartCheckout() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [cartItems, setCartItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [loadingCities, setLoadingCities] = useState(true);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderChange, setOrderChange] = useState(false);
  const [orderConfirme, setOrderConfirme] = useState(false);
  const [orderUnlockable, setOrderUnlockable] = useState(true);
  
  const userCookie = Cookies.get("user");
  const [sallerId, setSallerId] = useState("");

  // Initialize form data with proper types
  const [formData, setFormData] = useState({
    nom: "",
    phone: "", 
    address: "",
    city: null,
    delivery_price: 0,
    change_price: 0,
    comment: "",
    orders: [],
  });

  // Parse user cookie once on mount
  useEffect(() => {
    try {
      const userData = userCookie ? JSON.parse(userCookie) : null;
      setSallerId(userData?.id || "");
    } catch (error) {
      console.error("Error parsing user cookie:", error);
      setSallerId("");
    }
  }, [userCookie]);

  // Memoized city options for better performance
  const cityOptions = useMemo(() => 
    cities.map((city) => ({
      value: city.id,
      label: city.name,
      deliver: city.deliver,
      return: city.return,
      refuse: city.refuse,
      change: city.change,
    }))
  , [cities]);

  
  const { subtotal, shipping, total, totalProfit } = useMemo(() => {
    // Calculate total selling price (sellingPrice × quantity for all items)
    const totalSellingPrice = cartItems.reduce(
      (sum, item) => sum + (parseFloat(item.sellingPrice || item.price || 0) * parseInt(item.quantity || 0)),
      0
    );

    // Calculate total original price (price × quantity for all items)
    const totalOriginalPrice = cartItems.reduce(
      (sum, item) => sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0)),
      0
    );

    const shipping = parseFloat(orderChange ? formData.change_price : formData.delivery_price) || 0;
    const total = totalSellingPrice ;
    
    // Profit = (Total Selling Price) - (Total Original Price) - Shipping
    const totalProfit = totalSellingPrice - totalOriginalPrice - shipping;
    
    return { 
      subtotal: totalSellingPrice, 
      shipping, 
      total, 
      totalProfit
    };
  }, [cartItems, formData.delivery_price, formData.change_price, orderChange]);

  // Update orders whenever cart items or options change
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      orders: cartItems.map((item) => ({
        productId: item.productId || item.id,
        quantity: item.quantity,
        original_saller_price: item.price || 0, 
        selling_price_per_unit: item.sellingPrice || 0, // Use the item's selling price
        variant_sections: [{
          selectedVariants: item.selectedVariants || item.variants || {}
        }]
      })),
    }));
  }, [cartItems, orderChange, orderUnlockable, orderConfirme]);

  // Fetch cities with error handling
  const fetchCities = useCallback(async () => {
    setLoadingCities(true);
    try {
      const response = await fetch(`${BASE_URL}/api/getallcities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch cities: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      setCities(result?.data || []);
    } catch (err) {
      console.error("Cities fetch error:", err);
      toast.error(err.message || t("cart.error_loading_cities"));
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, [token]);

  // Load cart from cookies on mount and fetch cities
  useEffect(() => {
    const loadCart = () => {
      const cookieCart = Cookies.get("cart");
      try {
        setCartItems(cookieCart ? JSON.parse(cookieCart) : []);
      } catch (err) {
        console.error("Cart parse error:", err);
        setCartItems([]);
        toast.error(t("cart.parse_cart_error"));
      }
    };

    loadCart();
    fetchCities();
    setMounted(true);
  }, [fetchCities]);

  // Save cart to cookies whenever cartItems change
  useEffect(() => {
    if (mounted) {
      Cookies.set("cart", JSON.stringify(cartItems), {
        expires: 7,
        sameSite: "lax",
        secure: process.env.NODE_ENV === "production",
      });
    }
  }, [cartItems, mounted]);

  // Update quantity for cart item
  const updateQuantity = useCallback((itemKey, newQuantity) => {
    setCartItems(prev => 
      newQuantity <= 0 
        ? prev.filter(item => item.key !== itemKey)
        : prev.map(item => 
            item.key === itemKey ? { ...item, quantity: newQuantity } : item
          )
    );
  }, []);

  // Remove item from cart
  const removeItem = useCallback((itemKey) => {
    setCartItems(prev => prev.filter(item => item.key !== itemKey));
  }, []);

  // Update form data on input change
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSellingPriceChange = (itemKey, newPrice) => {
    setCartItems(prev => 
      prev.map(item => 
        item.key === itemKey 
          ? { ...item, sellingPrice: parseFloat(newPrice) || 0 }
          : item
      )
    );
  };

  // Handle city select change
  const handleCityChange = useCallback((selectedOption) => {
    const deliveryPrice = orderChange ? selectedOption?.change : selectedOption?.deliver;
    
    setFormData(prev => ({
      ...prev,
      city: selectedOption,
      delivery_price: deliveryPrice || 0,
      change_price: selectedOption?.change || 0,
    }));
  }, [orderChange]);

  // Handle change option toggle
  const handleChangeToggle = useCallback((e) => {
    const isChecked = e.target.checked;
    setOrderChange(isChecked);
    
    if (formData.city) {
      const deliveryPrice = isChecked ? formData.city.change : formData.city.deliver;
      setFormData(prev => ({
        ...prev,
        delivery_price: deliveryPrice || 0
      }));
    }
  }, [formData.city]);

  const renderVariants = useCallback((variants) => {
    if (!variants || Object.keys(variants).length === 0) return null;

    return (
      <div className="flex flex-wrap gap-4 text-xs ml-19 text-gray-500">
        {Object.entries(variants).map(([type, variant]) => (
          <div key={type} className="flex items-center gap-1 capitalize">
            <span className="font-medium">{type} :</span>
            {variant.type === "color" ? (
              <>
                <span
                  className="inline-block w-3 h-3 rounded-full ml-1 border border-gray-300"
                  style={{ backgroundColor: variant.value }}
                />
                <span>{variant.name}</span>
              </>
            ) : (
              <span>{variant.name || variant.value}</span>
            )}
          </div>
        ))}
      </div>
    );
  }, []);

  // Submit handler with proper validation
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmittingOrder(true);

    // Validation
    if (!formData.nom || !formData.phone || !formData.address || !formData.city) {
      toast.error(t("cart.fill_required_fields"));
      setSubmittingOrder(false);
      return;
    }
    
    if (cartItems.length === 0) {
      toast.error(t("cart.cart_empty_order"));
      setSubmittingOrder(false);
      return;
    }

    // Validate selling prices
    const hasInvalidPrices = cartItems.some(item => 
      !item.sellingPrice || isNaN(item.sellingPrice) || item.sellingPrice <= 0
    );
    
    if (hasInvalidPrices) {
      toast.error(t("cart.check_selling_prices"));
      setSubmittingOrder(false);
      return;
    }

    const sellingTotal = cartItems.reduce(
      (sum, item) => sum + (parseFloat(item.sellingPrice || item.price || 0) * parseInt(item.quantity || 0)),
      0
    );
    const originalTotal = cartItems.reduce(
      (sum, item) => sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0)),
      0
    );
    const effectiveDelivery = orderChange ? (parseFloat(formData.change_price) || 0) : (parseFloat(formData.delivery_price) || 0);
    const orderProfit = sellingTotal - originalTotal - effectiveDelivery;

    if (orderProfit < 10) {
      toast.error(t("cart.profit_min_error"));
      setSubmittingOrder(false);
      return;
    }

    // Construct payload
    const payload = [{
      saller_id: sallerId,
      name: formData.nom, 
      phone: formData.phone,
      address: formData.address,
      city: formData.city.label,
      comment: formData.comment,
      orders: formData.orders,
      delivery_price: formData.delivery_price,
      change_price: formData.change_price,
      change: orderChange,
      unlockable: orderUnlockable,
      confirme: orderConfirme,
    }];
    console.log(payload);
    

    try {
      const response = await fetch(`${BASE_URL}/api/store-cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `Failed to submit order: ${response.statusText}`);
      }

      toast.success(t("cart.order_submitted"));
      
      setCartItems([]);
      Cookies.remove("cart");
      setFormData({
        nom: "",
        phone: "",
        address: "",
        city: null,
        delivery_price: 0,
        change_price: 0,
        comment: "",
        orders: [],
      });
    } catch (err) {
      console.error("Order submission error:", err);
      toast.error(err.message || t("cart.order_submit_error"));
    } finally {
      setSubmittingOrder(false);
    }
  }, [formData, cartItems, orderChange, orderUnlockable, orderConfirme, sallerId, token]);

  if (!mounted) return null;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingBag size={35} className="text-[var(--color-primary-500)]" />
            {t('cart.your_cart')}
          </h1>
          <p className="text-gray-600 mt-2 text-sm">
            {t('cart.check_items')}
          </p>
        </div>

        <div className="grid lg:grid-cols-[58%_40%] gap-4">
          {/* Cart Items */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {t('cart.items_in_cart').replace('{count}', cartItems.length)}
            </h2>

            <div className="space-y-4">
              {cartItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('cart.cart_empty')}
                </p>
              ) : (
                cartItems.map((item) => (
                  <div key={item.key} className="py-2 px-4 border border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-colors">
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <img
                        src={`${BASE_URL}/storage/${item.image}` || "https://placehold.co/64x64/E2E8F0/64748B?text=No+Image"}
                        alt={item.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://placehold.co/64x64/E2E8F0/64748B?text=No+Image";
                        }}
                        className="w-16 h-16 object-cover rounded-lg"
                      />

                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {item.name}
                        </h3>
                        <p className="font-bold text-[var(--color-primary-500)] text-sm">
                        {(item.price || item.sellingPrice || 0).toFixed(2)} MAD
                        </p>
                      </div>

                      <div className="mr-2">
                        <label htmlFor={`selling-price-${item.key}`} className="block font-semibold text-gray-900 mb-1 text-xs">
                          {t('cart.selling_price_label')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          id={`selling-price-${item.key}`}
                          value={item.sellingPrice || ""}
                          onChange={(e) => handleSellingPriceChange(item.key, e.target.value)}
                          placeholder={t('cart.selling_price_placeholder')}
                          className="w-25 py-1.5 px-3 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                          min="0.01"
                          step="0.01"
                          required
                        />
                      </div>

                      <div className="flex items-center gap-3 pt-4 sm:mt-0">
                        <button
                          onClick={() => updateQuantity(item.key, item.quantity - 1)}
                          className="w-6 h-6 dark:text-white cursor-pointer rounded-full border border-gray-300 flex items-center justify-center hover:border-[var(--color-primary-500)] hover:text-[var(--color-primary-500)] transition-colors"
                          aria-label={t('cart.reduce_quantity')}
                        >
                          <Minus size={12} />
                        </button>

                        <span className="w-4 dark:text-white text-center text-xs">
                          {item.quantity}
                        </span>

                        <button
                          onClick={() => updateQuantity(item.key, item.quantity + 1)}
                          className="w-6 h-6 dark:text-white cursor-pointer rounded-full border border-gray-300 flex items-center justify-center hover:border-[var(--color-primary-500)] hover:text-[var(--color-primary-500)] transition-colors"
                          aria-label={t('cart.increase_quantity')}
                        >
                          <Plus size={12} />
                        </button>
                      </div>

                      <button
                        onClick={() => removeItem(item.key)}
                        className="text-gray-400 cursor-pointer hover:text-red-500 transition-colors ml-0 sm:ml-3 pt-4 sm:mt-0"
                        aria-label={t('cart.remove_item')}
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                    {renderVariants(item.selectedVariants || item.variants)}
                  </div>
                ))
              )}
            </div>

            {/* Order Summary */}
            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('cart.order_summary')}
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.subtotal_with_items').replace('{count}', cartItems.length)}</span>
                  <span>{subtotal.toFixed(2)} MAD</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.shipping')}</span>
                  <span>{shipping.toFixed(2)} MAD</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.estimated_profit')}</span>
                  <span className="font-semibold text-green-600">
                    {totalProfit.toFixed(2)} MAD
                  </span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-xl font-bold text-gray-900">
                    <span>{t('cart.total_client')}</span>
                    <span className="text-[var(--color-primary-500)]">{total.toFixed(2)} MAD</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2 text-xs">
                <Shield size={16} className="text-[var(--color-primary-500)]" />
                <span>{t('cart.secure_payment')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Truck size={16} className="text-[var(--color-primary-500)]" />
                <span>{t('cart.free_returns')}</span>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              {t('cart.checkout_details')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-5 mt-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-5">
                  <div className="mb-2 flex-grow">
                    <label
                      htmlFor="nom"
                      className="block font-semibold text-gray-900 mb-1 text-xs"
                    >
                      {t('cart.name_label')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      placeholder={t('cart.name_placeholder')}
                      className="w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                      required
                    />
                  </div>
                  <div className="mb-2 flex-grow">
                    <label
                      htmlFor="phone"
                      className="block font-semibold text-gray-900 mb-1 text-xs"
                    >
                      {t('cart.phone_label')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('cart.phone_placeholder')}
                      className="w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-5">
                  <div>
                    <div className="mb-2 flex-grow cursor-pointer">
                      <label
                        htmlFor="city"
                        className="block font-semibold text-gray-900 mb-1 text-xs"
                      >
                        {t('cart.city_label')} <span className="text-red-500">*</span>
                      </label>
                      <Select
                        inputId="city"
                        options={cityOptions}
                        value={formData.city}
                        onChange={handleCityChange}
                        placeholder={loadingCities ? t('cart.loading_cities') : t('cart.choose_city')}
                        className="w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            border: "none",
                            cursor: "pointer",
                          }),
                        }}
                        isSearchable
                        required
                        isLoading={loadingCities}
                        isDisabled={loadingCities}
                        noOptionsMessage={() => t('cart.no_cities_found')}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 flex-grow">
                      <label
                        htmlFor="address"
                        className="block font-semibold text-gray-900 mb-1 text-xs"
                      >
                        {t('cart.address_label')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder={t('cart.address_placeholder')}
                        className="w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-5">
                  <div>
                    <div className="space-y-4 max-lg:py-10 lg:pb-4 min-xl:py-10">
                      <div className="flex items-center gap-3 hidden">
                        <input
                          type="checkbox"
                          id="order-change"
                          checked={orderChange}
                          onChange={handleChangeToggle}
                          className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                        />
                        <label htmlFor="order-change" className="text-xs text-gray-700 cursor-pointer">
                          {t('cart.change')}
                        </label>
                      </div>
                      
                      <div className="flex items-center gap-3 hidden">
                        <input
                          type="checkbox"
                          id="order-unlockable"
                          checked={orderUnlockable}
                          onChange={(e) => setOrderUnlockable(e.target.checked)}
                          className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                        />
                        <label htmlFor="order-unlockable" className="text-xs text-gray-700 cursor-pointer">
                          {t('cart.unlockable')}
                        </label>
                      </div>

                      <div className="flex items-center gap-3 hidden">
                        <input
                          type="checkbox"
                          id="order-confirme"
                          checked={orderConfirme}
                          onChange={(e) => setOrderConfirme(e.target.checked)}
                          className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                        />
                        <label htmlFor="order-confirme" className="text-xs text-gray-700 cursor-pointer">
                          {t('cart.already_confirmed')}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="w-full">
                    <label
                      htmlFor="comment"
                      className="block font-semibold text-gray-900 mb-1 text-xs"
                    >
                      {t('cart.comment_label')}
                    </label>
                    <textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      placeholder={t('cart.comment_placeholder')}
                      className="w-full resize-none py-3 px-4 h-28 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                      maxLength={1000}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-1.5 border-t border-gray-200 mt-6">
                <div className="flex justify-between items-center max-md:block">
                  <div className="text-right block w-full">
                    <span className="font-semibold text-gray-900 block my-[-5px]">
                      {t('cart.total_client_colon')}{" "}
                      <span className="text-[var(--color-primary-500)] font-bold">
                        {total.toFixed(2)} MAD
                      </span>
                    </span>
                    <span className="text-sm">
                      <span className="text-green-600 font-semibold">
                        {t('cart.estimated_profit_colon')} {totalProfit.toFixed(2)} MAD
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  className="w-full bg-[var(--color-primary-500)] cursor-pointer text-white py-3 px-6 rounded-xl text-sm hover:bg-[#099c89] transition-colors duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={submittingOrder || cartItems.length === 0}
                >
                  {submittingOrder ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t('cart.submitting')}
                    </>
                  ) : (
                    t('cart.place_order')
                   )} 
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}